import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, MapPin, Sparkles, ChevronDown, ShieldCheck, FileText } from "lucide-react";
import Layout from "../components/Layout";
import { PLAN_BASE } from "../lib/pricing";

/**
 * Public /plans page — three-tier catalog matching the new Saver/Eco/Secure
 * lineup. Pricing is intentionally hidden here (it's address-dependent and
 * shown on /residential and inside enrollment). This page is a "menu":
 * choose a plan → land on /shop to verify the address → see real $.
 */

const STR = {
  es: {
    kicker: "Planes",
    h1Pre: "Tu plan",
    h1Accent: "perfecto",
    h1Post: ", a tu medida.",
    sub: "Tres planes, una promesa: tarifa fija basada en tu ESIID, sin sorpresas. Confirmamos tu precio en menos de 60 segundos.",
    selectCta: "Elegir este plan",
    fixedNote: "Precio personalizado",
    fixedSub: "Calculado con tu ESIID",
    perksTitle: "Incluye",
    legalTip: "Tu precio depende de tu utility (TDU) y consumo. Lo confirmamos en el paso siguiente.",
    recommended: "Recomendado",
    fixedRate: "Tarifa fija garantizada",
    address: {
      title: "Verifica tu dirección",
      sub: "Necesitamos tu ZIP o dirección para fijar el precio de tu plan.",
      cta: "Buscar mi precio",
    },
    moreInfo: "Más información del plan",
  },
  en: {
    kicker: "Plans",
    h1Pre: "Your plan",
    h1Accent: "tailored",
    h1Post: " to you.",
    sub: "Three plans, one promise: a fixed rate based on your ESIID, no surprises. We confirm your price in under 60 seconds.",
    selectCta: "Choose this plan",
    fixedNote: "Personalized price",
    fixedSub: "Calculated with your ESIID",
    perksTitle: "Includes",
    legalTip: "Your price depends on your utility (TDU) and usage. We'll confirm in the next step.",
    recommended: "Recommended",
    fixedRate: "Fixed rate guaranteed",
    address: {
      title: "Verify your address",
      sub: "We need your ZIP or address to lock in your plan's price.",
      cta: "Find my price",
    },
    moreInfo: "More plan info",
  },
};

// The "recommended" plan is Eco 12 (matches the eyebrow inside pricing.js).
// We keep the historical helper so it can read from the user's stored
// signals — for now it always returns "eco12" as the recommended default,
// which mirrors PLAN_BASE.accent === true.
function pickRecommendedTier() {
  return "eco12";
}

// Visual mapping per plan id — only the accent color differs by plan.
const PLAN_COLOR = {
  saver12:  { btn: "bg-velcor-green text-white hover:bg-velcor-green/90", check: "text-velcor-green" },
  eco12:    { btn: "bg-velcor-green text-white hover:bg-velcor-green/90", check: "text-emerald-600" },
  secure24: { btn: "bg-[#0f1d3d] text-white hover:bg-[#1a2b56]", check: "text-[#0f1d3d]" },
};

export default function Plans() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "en";
  const s = STR[lang];
  const navigate = useNavigate();

  const recommendedId = useMemo(pickRecommendedTier, []);

  const onSelect = (planId) => {
    localStorage.setItem("velcor_selected_plan", planId);
    navigate(`/shop?plan=${planId}`);
  };

  return (
    <Layout minimalFooter>
      <Helmet>
        <title>{s.kicker} — Velcor Energy</title>
      </Helmet>

      <section className="max-w-[1100px] mx-auto px-5 pt-10 md:pt-16 pb-20" data-testid="plans-page">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
          className="text-center mb-10 md:mb-14"
        >
          <span className="inline-flex items-center gap-1.5 rounded-full border border-velcor-green/25 bg-velcor-green/5 px-3 py-1 text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-velcor-green mb-4">
            <Sparkles className="w-3 h-3" /> {s.kicker}
          </span>
          <h1
            className="display text-velcor-ink mx-auto leading-[1.04]"
            style={{ fontSize: "clamp(32px, 4.6vw, 54px)", maxWidth: 760, letterSpacing: "-0.024em" }}
          >
            {s.h1Pre} <span className="display-accent">{s.h1Accent}</span>{s.h1Post}
          </h1>
          <p className="mt-4 text-slate-500 text-[14.5px] md:text-[16px] max-w-[600px] mx-auto leading-relaxed">
            {s.sub}
          </p>
        </motion.div>

        {/* Plans grid — 1 col mobile, 3 col desktop. Driven by PLAN_BASE
            (the single source of truth shared with PlanPage + admin). */}
        <div className="grid md:grid-cols-3 gap-4 md:gap-5">
          {PLAN_BASE.map((p, idx) => (
            <PlanTile
              key={p.id}
              plan={p}
              lang={lang}
              s={s}
              onSelect={onSelect}
              recommended={p.id === recommendedId}
              delay={0.18 + idx * 0.08}
            />
          ))}
        </div>

        {/* Address verification banner */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.5 }}
          className="mt-10 md:mt-14 bg-white rounded-2xl border-2 border-velcor-green/30 p-6 md:p-7 grid md:grid-cols-[1fr_auto] gap-5 items-center"
          data-testid="plans-address-banner"
        >
          <div className="flex items-start gap-3">
            <span className="grid place-items-center w-11 h-11 rounded-full bg-velcor-green/10 text-velcor-green shrink-0">
              <MapPin className="w-5 h-5" strokeWidth={2} />
            </span>
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-velcor-green mb-1">
                {s.fixedNote}
              </div>
              <h3 className="text-velcor-ink mb-1" style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.015em" }}>
                {s.address.title}
              </h3>
              <p className="text-slate-500 text-[13px] leading-relaxed max-w-[440px]">
                {s.address.sub}
              </p>
            </div>
          </div>
          <Link
            to="/shop"
            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-velcor-green text-white font-extrabold text-[13px] hover:bg-velcor-green/90 transition-colors shadow-[0_8px_20px_rgba(47,122,58,0.30)] shrink-0"
            data-testid="plans-find-price"
          >
            {s.address.cta} <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <p className="mt-5 text-center text-[11.5px] text-slate-400">{s.legalTip}</p>
      </section>
    </Layout>
  );
}

function PlanTile({ plan, lang, s, onSelect, recommended, delay = 0 }) {
  const [open, setOpen] = useState(false);
  const c = PLAN_COLOR[plan.id] || PLAN_COLOR.saver12;
  const term = lang === "en" ? plan.termEn : plan.term;
  const eyebrow = lang === "en" ? plan.eyebrowEn : plan.eyebrow;

  return (
    <motion.div
      initial={{ opacity: 0, y: 18, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay, ease: [0.2, 0.8, 0.2, 1] }}
      className={`group relative bg-white rounded-2xl p-5 md:p-6 flex flex-col shadow-[0_4px_18px_-12px_rgba(15,29,61,0.10)] ${
        recommended
          ? "ring-2 ring-velcor-green/60 shadow-[0_18px_46px_-26px_rgba(47,122,58,0.40)]"
          : "ring-1 ring-slate-200"
      }`}
      data-testid={`plan-tile-${plan.id}`}
    >
      {recommended && (
        <span
          className="absolute -top-2.5 left-5 inline-flex items-center gap-1 rounded-full bg-velcor-green text-white px-2.5 py-1 text-[9.5px] font-extrabold uppercase tracking-[0.16em] shadow-[0_6px_18px_-6px_rgba(47,122,58,0.6)]"
          data-testid={`plan-tile-${plan.id}-recommended`}
        >
          <Sparkles className="w-2.5 h-2.5" /> {s.recommended}
        </span>
      )}

      <div className="mb-3 mt-1">
        <div className="text-[9.5px] font-extrabold uppercase tracking-[0.14em] text-slate-400 mb-1">
          {eyebrow}
        </div>
        <h3 className="text-velcor-ink text-[17px]" style={{ fontWeight: 700, letterSpacing: "-0.015em" }}>
          {plan.name}
        </h3>
        <p className="text-[11.5px] text-slate-500 mt-0.5">{term}</p>
      </div>

      {/* Description block (no rate shown — pricing is address-dependent) */}
      <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-3 mb-3 text-center">
        <div className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">
          {s.fixedNote}
        </div>
        <p className="text-[12px] text-slate-700 leading-snug">{plan.description}</p>
        <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[9.5px] font-extrabold text-emerald-700 ring-1 ring-emerald-100">
          <ShieldCheck className="w-2.5 h-2.5" /> {s.fixedRate}
        </div>
      </div>

      <button
        type="button"
        onClick={() => onSelect(plan.id)}
        className={`w-full h-11 rounded-xl font-extrabold text-[13px] inline-flex items-center justify-center gap-2 transition-all ${c.btn}`}
        data-testid={`plan-tile-${plan.id}-cta`}
      >
        {s.selectCta} <ArrowRight className="w-4 h-4" />
      </button>

      {/* Inline expander */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-2 inline-flex items-center justify-center gap-1 text-[11px] font-extrabold text-slate-500 hover:text-velcor-green transition-colors py-1.5"
        aria-expanded={open}
        data-testid={`plan-tile-${plan.id}-details-toggle`}
      >
        <FileText className="w-3 h-3" />
        {s.moreInfo}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="details"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
            data-testid={`plan-tile-${plan.id}-details`}
          >
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
              <div>
                <div className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">
                  {s.perksTitle}
                </div>
                <ul className="space-y-1.5 text-[12.5px] text-slate-700">
                  {plan.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <Check className={`w-3 h-3 mt-0.5 shrink-0 ${c.check}`} strokeWidth={2.6} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
